import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: '首页', path: '/' },
  { label: '分类浏览', path: '/category/all' },
  { label: '热门资讯', path: '/articles' },
  { label: '关于我们', path: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-play-circle-fill text-xl text-white"></i>
            </div>
            <span className={`text-xl font-heading font-bold tracking-wide ${
              scrolled || !isHome ? 'text-foreground-900' : 'text-white'
            }`}>
              豆花视频
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = link.path === '/' 
                ? location.pathname === '/' 
                : location.pathname.startsWith(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    isActive
                      ? scrolled || !isHome
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-white/20 text-white'
                      : scrolled || !isHome
                        ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop Right Actions */}
          <div className="hidden md:flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              {searchOpen ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    placeholder="搜索视频、剧集..."
                    className="w-48 px-3 py-1.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    autoFocus
                    onBlur={() => setSearchOpen(false)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setSearchOpen(false); }}
                  />
                </div>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                    scrolled || !isHome
                      ? 'text-foreground-500 hover:text-foreground-900 hover:bg-background-100'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  aria-label="搜索"
                >
                  <i className="ri-search-line text-lg"></i>
                </button>
              )}
            </div>

            {/* Download App */}
            <a
              href="#download"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                scrolled || !isHome
                  ? 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  : 'bg-white/15 text-white hover:bg-white/25'
              }`}
            >
              <i className="ri-download-2-line mr-1.5"></i>
              下载APP
            </a>

            {/* VIP */}
            <Link
              to="/vip"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-vip-crown-line mr-1.5"></i>
              开通会员
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled || !isHome
                ? 'text-foreground-700 hover:bg-background-100'
                : 'text-white hover:bg-white/10'
            }`}
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 pb-4 pt-2">
            <div className="flex flex-col gap-1 px-2">
              {/* Mobile Search */}
              <div className="px-2 py-1.5">
                <input
                  type="text"
                  placeholder="搜索视频、剧集..."
                  className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>
              {navLinks.map((link) => {
                const isActive = link.path === '/' 
                  ? location.pathname === '/' 
                  : location.pathname.startsWith(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-3 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isActive
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="flex gap-2 mt-2 px-2">
                <a
                  href="#download"
                  className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors text-center cursor-pointer"
                >
                  <i className="ri-download-2-line mr-1"></i>下载APP
                </a>
                <Link
                  to="/vip"
                  className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors text-center cursor-pointer"
                >
                  <i className="ri-vip-crown-line mr-1"></i>开通会员
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}